import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  APP_NAME, CONTACT_EMAIL, EDITEUR, LEGAL_UPDATED_AT, LEGAL_VERSION,
} from '../constants/site';
import './Legal.css';

/**
 * Conditions générales d'utilisation — version phase de test (MVP gratuit).
 *
 * Rédigées en interne, pas encore relues par le conseil (Me Lechien, cabinet
 * LexIC). Principe retenu : ne rien affirmer qui ne soit vrai à date. En
 * particulier, aucune clause tarifaire ni de résiliation d'abonnement tant que
 * rien n'est facturé (ni B2C, ni B2B).
 *
 * ⚠️ Art. 4 (âge) énonce une règle, pas un mécanisme : aucun contrôle d'âge
 * n'est implémenté à l'inscription et le module d'étude (`forms/`) accepte
 * explicitement la catégorie « mineure ». Un vrai recueil d'autorisation
 * parentale reste à construire — c'est un point critique identifié de l'audit.
 */
const ConditionsGenerales = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <button className="legal-page__back-btn" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="legal-page__title">Conditions générales</h1>
      </div>

      <div className="legal-page__card">
        <p className="legal-page__section-text">
          Les présentes conditions générales d'utilisation (CGU) régissent l'accès et l'utilisation
          de l'application CycleGala. En créant un compte, vous les acceptez.
        </p>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">1. Éditeur</h2>
          <p className="legal-page__section-text">
            L'application {APP_NAME} est éditée par <strong>{EDITEUR.nom}</strong>, {EDITEUR.forme} au
            capital de {EDITEUR.capital}, immatriculée au Registre du Commerce et des Sociétés
            de {EDITEUR.rcsVille} sous le numéro {EDITEUR.rcsNumero}, dont le siège social est
            situé {EDITEUR.siege}. {APP_NAME} est le nom commercial sous lequel {EDITEUR.nom} exploite
            l'application.
          </p>
          <p className="legal-page__section-text">
            Contact : <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">2. Objet du service</h2>
          <p className="legal-page__section-text">
            CycleGala est un outil de suivi du cycle menstruel, de l'état mental et de l'activité
            sportive. À partir des informations que vous saisissez, l'application produit des
            indicateurs de suivi et des suggestions d'adaptation de l'entraînement.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">3. Absence de finalité médicale</h2>
          <p className="legal-page__section-text">
            CycleGala n'est pas un dispositif médical. Les indicateurs et suggestions fournis sont
            <strong> indicatifs</strong> : ils ne constituent ni un diagnostic, ni une prescription,
            ni un avis médical, et ne peuvent se substituer à une consultation auprès d'un
            professionnel de santé.
          </p>
          <p className="legal-page__section-text">
            En particulier, les prédictions relatives au cycle ou à l'ovulation ne doivent en aucun
            cas être utilisées comme moyen de contraception ou d'aide à la conception. Pour toute
            question de santé, consultez un professionnel.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">4. Accès au service et âge requis</h2>
          <p className="legal-page__section-text">
            L'application est actuellement en phase de test et l'accès se fait sur invitation, dans
            le cadre de partenariats avec des clubs. La création d'un compte est nécessaire.
          </p>
          <p className="legal-page__section-text">
            Si vous avez moins de 18 ans, vous devez disposer de l'autorisation préalable de votre
            représentant légal pour créer un compte et pour que vos données de santé soient traitées.
            Votre représentant légal peut à tout moment demander la suppression du compte à l'adresse
            indiquée à l'article 1.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">5. Gratuité</h2>
          <p className="legal-page__section-text">
            Pendant la phase de test, l'accès à l'application est gratuit. Aucun paiement n'est
            demandé et aucun abonnement n'est souscrit. Si une offre payante devait être introduite,
            elle ferait l'objet de conditions distinctes, portées à votre connaissance et soumises à
            votre acceptation préalable.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">6. Votre compte</h2>
          <p className="legal-page__section-text">
            Vous vous engagez à fournir des informations exactes et à préserver la confidentialité de
            vos identifiants. Toute activité effectuée depuis votre compte est réputée émaner de
            vous. Prévenez-nous sans délai en cas d'utilisation non autorisée.
          </p>
          <p className="legal-page__section-text">
            Vous pouvez demander la suppression de votre compte à tout moment en écrivant à
            l'adresse indiquée à l'article 1.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">7. Vos données</h2>
          <p className="legal-page__section-text">
            Les données que vous saisissez vous appartiennent. Leur traitement, leur chiffrement,
            leur durée de conservation et les droits dont vous disposez sont décrits dans la
            politique de confidentialité, accessible depuis votre profil.
          </p>
          <p className="legal-page__section-text">
            Le partage de vos données avec un coach est facultatif : il ne se produit que si vous
            l'activez, domaine par domaine, et vous pouvez le révoquer à tout moment.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">8. Disponibilité du service</h2>
          <p className="legal-page__section-text">
            L'application étant en phase de test, elle peut évoluer, être interrompue
            temporairement ou faire l'objet de corrections sans préavis. Nous ne garantissons ni
            une disponibilité continue, ni l'absence d'erreur. Nous vous recommandons de ne pas
            considérer l'application comme votre unique moyen de conservation de vos données.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">9. Utilisation loyale</h2>
          <p className="legal-page__section-text">
            Vous vous engagez à ne pas tenter d'accéder aux données d'autres utilisateurs, à ne pas
            perturber le fonctionnement du service et à ne pas en extraire le contenu de manière
            automatisée. En cas de manquement, l'accès au compte peut être suspendu.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">10. Propriété intellectuelle</h2>
          <p className="legal-page__section-text">
            L'application, son code, son design et ses contenus sont la propriété de CycleGala.
            Cette propriété ne porte pas sur les données que vous saisissez, qui restent les vôtres.
            Toute reproduction sans autorisation est interdite.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">11. Responsabilité</h2>
          <p className="legal-page__section-text">
            CycleGala met en œuvre les moyens raisonnables pour assurer le bon fonctionnement du
            service et la sécurité des données. Compte tenu du caractère indicatif des informations
            fournies (article 3) et du stade de test du service (article 8), CycleGala ne peut être
            tenue responsable des décisions d'entraînement ou de santé prises sur la seule base de
            l'application.
          </p>
          <p className="legal-page__section-text">
            Aucune stipulation des présentes ne limite les droits que vous tenez de la loi.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">12. Modification des CGU</h2>
          <p className="legal-page__section-text">
            Les présentes conditions peuvent être modifiées. Toute modification substantielle vous
            sera signalée dans l'application, et votre acceptation sera de nouveau recueillie
            lorsque la loi l'exige.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">13. Droit applicable</h2>
          <p className="legal-page__section-text">
            Les présentes conditions sont soumises au droit français. En cas de différend, une
            solution amiable sera recherchée avant toute action judiciaire.
          </p>
        </div>
      </div>

      <p className="legal-page__version">Version {LEGAL_VERSION} — {LEGAL_UPDATED_AT}</p>
    </div>
  );
};

export default ConditionsGenerales;
